// @ts-strict-ignore
import { getNumericPathIndices } from 'flow/service/actions';
import type { ExpressionTerm } from '../../../store/engine/types';
import { CaretPositionSuffixTypes, RuleEditorDropdownMode } from '../NodeEditorDrawer/types';
import type { UserActionObject } from '../NodeEditorDrawer/types';
import {
  getGroupStartIndexOffset,
  getIncrementedPositionIndex,
  getIsCaretPositionAtEnd,
  getIsCaretPositionInInitialInsertionMode,
  getIsCaretPositionInInsertionMode,
  isCaretPositionStringOnDataRouteItem,
  isCaretPositionStringOnGroupEndParenthesis,
  isCaretPositionStringOnGroupStartParenthesis,
  scrubInsertionModeFromCaret,
} from './helpers';
import { startingCaretPosition } from './constants';

export const getAllPossibleGroupClosingPaths = ({
  itemsWithinMaxRange,
  pendingGroupPathIndices,
  pendingGroupSide = 'after',
  parentOfGroupOpeningPath,
}: {
  itemsWithinMaxRange: (ExpressionTerm | ExpressionTerm[])[];
  pendingGroupPathIndices: string[];
  pendingGroupSide: 'after' | 'before';
  parentOfGroupOpeningPath: string;
}) => {
  const previousIndexValue = Number(pendingGroupPathIndices.at(-1));
  const indexOffset = getGroupStartIndexOffset(pendingGroupSide);

  return itemsWithinMaxRange.map((possibleGroupItem, index) => (
    `${parentOfGroupOpeningPath}[${previousIndexValue + index + indexOffset}]`
  ));
};

export const getPreviousExpressionCaretPosition = ({
  indexOfCurrentCaretPositionInFlatIndices,
  morphingCaretPosition,
  ruleEditorFlatCaretIndices,
  userActionObject,
}: {
  indexOfCurrentCaretPositionInFlatIndices: number | undefined;
  morphingCaretPosition: string | undefined;
  ruleEditorFlatCaretIndices: string[];
  userActionObject?: UserActionObject;
}) => {
  // newPositionString: when we can update position directly
  // newPositionIndexForDelayedUpdate: when we have to update position after flat indices are regenerated
  if (getIsCaretPositionInInsertionMode(morphingCaretPosition)) {
    return {
      newPositionString: getIsCaretPositionInInitialInsertionMode(morphingCaretPosition)
        ? startingCaretPosition
        : scrubInsertionModeFromCaret(morphingCaretPosition),
      newPositionIndexForDelayedUpdate: undefined,
    };
  }

  if (indexOfCurrentCaretPositionInFlatIndices === 0) {
    return {
      newPositionString: startingCaretPosition,
      newPositionIndexForDelayedUpdate: undefined,
    };
  }

  const previousIndex = ruleEditorFlatCaretIndices[
    indexOfCurrentCaretPositionInFlatIndices - 1];

  if (isCaretPositionStringOnDataRouteItem(previousIndex)) {
    if (userActionObject?.mode === 'deletion') { // this func was fired because of deleting an item
      if (isCaretPositionStringOnDataRouteItem(morphingCaretPosition)
        || isCaretPositionStringOnGroupEndParenthesis(morphingCaretPosition)) {
        // if the current caret position was on one of dataroute dropdowns OR group ending parenthesis ')'
        return {
          newPositionString: undefined,
          newPositionIndexForDelayedUpdate: String(indexOfCurrentCaretPositionInFlatIndices - 2),
        };
      }

      return {
        newPositionString: undefined,
        newPositionIndexForDelayedUpdate: String(indexOfCurrentCaretPositionInFlatIndices - 1),
      };
    }

    if (!isCaretPositionStringOnGroupEndParenthesis(morphingCaretPosition)) {
      if (isCaretPositionStringOnDataRouteItem(morphingCaretPosition)) {
        return {
          newPositionString: previousIndex,
          newPositionIndexForDelayedUpdate: undefined,
        };
      }
    }
  }

  if (
    isCaretPositionStringOnGroupEndParenthesis(morphingCaretPosition)
    && isCaretPositionStringOnGroupStartParenthesis(previousIndex)
  ) {
    if (userActionObject?.mode === 'deletion') { // this func was fired because of deleting an item
      return {
        newPositionString: undefined,
        newPositionIndexForDelayedUpdate: String(indexOfCurrentCaretPositionInFlatIndices - 2),
      };
    }
  }

  return {
    newPositionString: `${previousIndex}${CaretPositionSuffixTypes.CARET_INSERTION}`,
    newPositionIndexForDelayedUpdate: undefined,
  };
};


export const getNextExpressionCaretPosition = ({
  pendingGroupPathInFlatCaretIndexFormat,
  indexOfCurrentCaretPositionInFlatIndices,
  getDataRoutePositionString,
  morphingCaretPosition,
  ruleEditorFlatCaretIndices,
  userActionObject,
}: {
  pendingGroupPathInFlatCaretIndexFormat: string | undefined;
  indexOfCurrentCaretPositionInFlatIndices: number | undefined;
  getDataRoutePositionString: (newPosIndex: number, actionPublicId: string) => string;
  morphingCaretPosition: string | undefined;
  ruleEditorFlatCaretIndices: string[];
  userActionObject?: UserActionObject;
}) => {
  let newPositionString; // this is for when we can update position directly
  let newPositionIndexForDelayedUpdate; // this is for when we have to update position after flat indices are regenerated

  const nextIndex = ruleEditorFlatCaretIndices[indexOfCurrentCaretPositionInFlatIndices + 1];
  if (!nextIndex) {
    let newPosIndex = ruleEditorFlatCaretIndices.at(-1);
    if (!isNaN(Number(newPosIndex))) {
      // when the current item is just number e.g. 0 / 1 / 2
      const newPosIdxNumberIncremented = userActionObject?.value
        ? Number(newPosIndex) + 1
        : Number(newPosIndex);

      if (userActionObject?.value?.includes('action_')) {
        newPositionString = getDataRoutePositionString(
          newPosIdxNumberIncremented, userActionObject?.value);
      } else if (userActionObject?.value === '(') {
        newPositionString = `${newPosIdxNumberIncremented}${CaretPositionSuffixTypes.GROUP_START}${CaretPositionSuffixTypes.CARET_INSERTION}`;
      } else {
        newPositionString = `${newPosIdxNumberIncremented}${CaretPositionSuffixTypes.CARET_INSERTION}`;
      }
    } else if (newPosIndex) {
      // when the current item is not just number e.g. 0-1!dataRoute / 0-1!groupEnd / 0-2!caret
      if (getIsCaretPositionAtEnd(ruleEditorFlatCaretIndices, morphingCaretPosition)
        && !['operator', 'grouping'].includes(userActionObject?.meta)) {
        // if caret was at the end and also was in the insertion mode (ends with '!caret')
        newPositionString = `${morphingCaretPosition}${CaretPositionSuffixTypes.CARET_INSERTION}`;
      } else {
        // this splits "0-1" into ["0", "1"]
        const positionIndexArray = newPosIndex.split('-');

        // we'll grab the ... item from that array and bump the number
        // if 2nd last item exists in the array, we'll grab that, otherwise, the last item in the array
        // no 2nd last item in the array = the caret index is a single number string ("6") meaning not part of a group or data route
        newPosIndex = positionIndexArray.at(-2)
          ? String(Number(getNumericPathIndices(positionIndexArray.at(-2))[0]) + 1)
          : String(Number(getNumericPathIndices(positionIndexArray.at(-1))[0]) + 1);

        if (userActionObject?.value) {
          if (userActionObject?.value === 'ungroup') {
            const newPositionBackwardJumpCount = isCaretPositionStringOnGroupEndParenthesis(
              morphingCaretPosition) ? 2 : 1;
            newPositionIndexForDelayedUpdate = String(
              indexOfCurrentCaretPositionInFlatIndices - newPositionBackwardJumpCount);
          } else {
            newPositionIndexForDelayedUpdate = getIncrementedPositionIndex({
              userActionObject,
              indexOfCurrentCaretPositionInFlatIndices,
            });
          }
        } else {
          newPositionString = startingCaretPosition;
        }
      }
    } else if (morphingCaretPosition === startingCaretPosition) {
      if (userActionObject?.mode === RuleEditorDropdownMode.CARET_EXPRESSION) { // this func was called after insertion happened
        newPositionIndexForDelayedUpdate = getIncrementedPositionIndex({
          userActionObject,
          indexOfCurrentCaretPositionInFlatIndices,
        });
      }
    } else {
      newPositionString = startingCaretPosition;
    }

    return {
      newPositionString,
      newPositionIndexForDelayedUpdate,
    };
  }

  if (morphingCaretPosition.endsWith(CaretPositionSuffixTypes.CARET_INSERTION)) {
    if (
      isCaretPositionStringOnGroupStartParenthesis(morphingCaretPosition)
      && pendingGroupPathInFlatCaretIndexFormat === morphingCaretPosition.replace(RuleEditorDropdownMode.GROUP_START, '')
    ) {
      // if the caret current position is at the group opening parenthesis '(' + "!caret" caret insertion position and
      // the pending group path in a flat caret index format is also in the same spot as that.
      // meaning user opened an unclosed pending group (somewhere in between expression items) and the caret was on `(`
      // and then user pressed right arrow key to go "right". in this case, we should always jump to next index
      newPositionString = `${nextIndex}`;
    } else if (userActionObject?.mode === RuleEditorDropdownMode.CARET_EXPRESSION) { // this func was called after insertion happened
      let numberToIncrement = 1;
      if (userActionObject?.value?.includes('action_')) {
        if (userActionObject?.value.includes('service')) {
          numberToIncrement = 2;
        }
      }
      newPositionIndexForDelayedUpdate = String(
        indexOfCurrentCaretPositionInFlatIndices + numberToIncrement,
      );
    } else {
      newPositionString = `${nextIndex}`;
    }
  } else if (
    (
      isCaretPositionStringOnDataRouteItem(nextIndex)
      && isCaretPositionStringOnDataRouteItem(morphingCaretPosition)
    )
    || isCaretPositionStringOnGroupStartParenthesis(nextIndex)
  ) {
    newPositionString = `${nextIndex}`;
  } else if (userActionObject?.meta === 'grouping'
    && ['ungroup', 'cancel_pending_group'].includes(userActionObject?.value)
  ) {
    // user is ungrouping, the caret indices would change as a result, hence we need to calculate next position by index number
    newPositionIndexForDelayedUpdate = String(indexOfCurrentCaretPositionInFlatIndices - 1);
  } else {
    newPositionString = `${morphingCaretPosition}${CaretPositionSuffixTypes.CARET_INSERTION}`;
  }

  return {
    newPositionString,
    newPositionIndexForDelayedUpdate,
  };
};
